import {
  Alert,
  Anchor,
  Badge,
  Button,
  Container,
  Group,
  Stack,
  Switch,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useEnabledModules } from "../features/modules/useEnabledModules";
import { QueueFilters } from "../features/queue/QueueFilters";
import { QueueSection } from "../features/queue/QueueSection";
import { QuotaIndicator } from "../features/queue/QuotaIndicator";
import type {
  SortOrder,
  TaskSortField,
  TaskStatus,
} from "../features/queue/api";
import { isNoRunnerConflict } from "../features/queue/provisioning";
import { requestErrorText } from "../features/queue/requestErrorText";
import { hasUnrecordedPrAttempt } from "../features/queue/unrecordedPrAttempt";
import { useCreateTask } from "../features/queue/useCreateTask";
import { useQueue } from "../features/queue/useQueue";
import { useTasks } from "../features/queue/useTasks";

// Бейдж Mantine по умолчанию усекает текст ellipsis'ом (и в root, и
// в label), из-за чего длинные локализованные подписи в узких колонках
// таблицы теряют смысл. Разрешаем перенос: root растёт по высоте,
// label переносит строки вместо ellipsis.
const noTruncateBadgeStyles = {
  root: { height: "auto" },
  label: { whiteSpace: "normal", overflow: "visible" },
} as const;

// Страница «Очередь».
//
// Страница защищена `<ProtectedRoute>`, org_id берётся из JWT-токена
// в `authedFetch`. Список задач из `useTasks`, активная подсвечена
// сравнением с `useQueue().active_task_id`. Кнопка «Создать
// тестовую задачу» делает POST.
export function QueuePage(): ReactElement {
  const { t } = useTranslation();
  // Страница смонтирована под `RequireModule`, набор уже загружен;
  // квота — отдельный модуль `quota`, гейтится независимо от `task`.
  const modules = useEnabledModules();

  const [activeOnly, setActiveOnly] = useState(false);
  const [statuses, setStatuses] = useState<TaskStatus[]>([]);
  const [sort, setSort] = useState<TaskSortField>("created_at");
  const [order, setOrder] = useState<SortOrder>("asc");
  const tasks = useTasks({ activeOnly, statuses, sort, order });
  const queue = useQueue();
  const createMutation = useCreateTask();

  const handleCreate = (): void => {
    createMutation.mutate({
      title: t("queue.createPlaceholder"),
      kind: "atomic",
    });
  };

  const queueError = queue.error;
  const activeTaskId = queue.data?.queue?.active_task_id ?? null;
  const queueMissing = queue.data?.queue === null;
  const createError = createMutation.error;

  return (
    <Container py="xl">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={1}>{t("queue.title")}</Title>
          <Anchor component={Link} to="/self-improvement">
            {t("queue.selfImprovementLink")}
          </Anchor>
        </Group>

        {modules.isEnabled("quota") ? (
          <QuotaIndicator
            pausedByQuota={queue.data?.queue?.paused_by_quota ?? false}
          />
        ) : null}

        <QueueSection
          state={queue.data}
          error={queueError}
          hasActiveTask={activeTaskId !== null}
        />

        <Group justify="space-between">
          <Button
            onClick={handleCreate}
            loading={createMutation.isPending}
            disabled={queueMissing}
            title={queueMissing ? t("queue.createDisabledHint") : undefined}
          >
            {t("queue.createButton")}
          </Button>
          <Switch
            label={t("queue.activeOnly")}
            checked={activeOnly}
            onChange={(event) => {
              setActiveOnly(event.currentTarget.checked);
            }}
          />
        </Group>

        {createError !== null ? (
          <Alert color="red" title={t("queue.createErrorTitle")}>
            {isNoRunnerConflict(createError)
              ? t("queue.createErrorUnprovisioned")
              : requestErrorText(createError, t)}
          </Alert>
        ) : null}

        <QueueFilters
          statuses={statuses}
          onStatusesChange={setStatuses}
          sort={sort}
          onSortChange={setSort}
          order={order}
          onOrderChange={setOrder}
        />

        {tasks.isError ? (
          <Alert color="red" title={t("queue.errorHeading")}>
            {requestErrorText(tasks.error, t)}
          </Alert>
        ) : null}

        <Table withTableBorder withColumnBorders striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("queue.colTitle")}</Table.Th>
              <Table.Th>{t("queue.colStatus")}</Table.Th>
              <Table.Th>{t("queue.colKind")}</Table.Th>
              <Table.Th>{t("queue.colActionable")}</Table.Th>
              <Table.Th>{t("queue.colActive")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {tasks.data?.map((task) => {
              const isActive = task.id === activeTaskId;
              const isAwaiting =
                task.status === "awaiting_approval" ||
                task.status === "awaiting_plan_approval";
              const statusLabel = t(`taskStatus.${task.status}`);
              const rowProps = isActive
                ? { bg: "var(--mantine-color-blue-light)" }
                : isAwaiting
                  ? { bg: "var(--mantine-color-yellow-light)" }
                  : {};
              return (
                <Table.Tr key={task.id} {...rowProps}>
                  <Table.Td>
                    <Anchor component={Link} to={`/tasks/${task.id}`}>
                      {task.title}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Badge
                        variant="light"
                        title={statusLabel}
                        styles={noTruncateBadgeStyles}
                      >
                        {statusLabel}
                      </Badge>
                      {hasUnrecordedPrAttempt(task) ? (
                        <Badge
                          color="orange"
                          variant="light"
                          title={t("queue.prAttemptUnrecordedHint")}
                          styles={noTruncateBadgeStyles}
                        >
                          {t("queue.prAttemptUnrecorded")}
                        </Badge>
                      ) : null}
                    </Group>
                  </Table.Td>
                  <Table.Td>{task.kind}</Table.Td>
                  <Table.Td>
                    {task.is_actionable ? (
                      <Badge
                        color="teal"
                        variant="light"
                        title={t("queue.actionableYes")}
                        styles={noTruncateBadgeStyles}
                      >
                        {t("queue.actionableYes")}
                      </Badge>
                    ) : (
                      <Text c="dimmed" size="sm">
                        —
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {isActive ? (
                      <Badge
                        color="blue"
                        title={t("queue.activeMarker")}
                        styles={noTruncateBadgeStyles}
                      >
                        {t("queue.activeMarker")}
                      </Badge>
                    ) : (
                      <Text c="dimmed" size="sm">
                        —
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>

        {tasks.data?.length === 0 ? (
          <Text c="dimmed">{t("queue.empty")}</Text>
        ) : null}
      </Stack>
    </Container>
  );
}
